import React, {
  useState,
  useContext,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { useParams } from "react-router-dom";
import { useCatalogue } from "../../../hooks/useCatalogue";
import Table from "./components/Table";
import useExcelParser from "./components/useExcelParser";
import { ValueContext } from "../../../Context";
import Loader from "../../../components/Loader";
import { API_BASE_URL, authFetch } from "../../../config/api";
import "./level.css";

const dataCache = new Map();
const CACHE_DURATION = 5 * 60 * 1000;

function Level() {
  const { level, semester, class_id, session } = useParams();
  const [load, setLoad] = useState(false);
  const [showExternal, setShowExternal] = useState(false);
  const { data, parseExcel } = useExcelParser();
  const { setAlert } = useContext(ValueContext);

  // professional course fields
  const [selectedCourse, setSelectedCourse] = useState("");
  const [selectedFileName, setSelectedFileName] = useState("");

  // external course fields
  const [selectedExternalCourse, setSelectedExternalCourse] = useState("");
  const [isManualExternal, setIsManualExternal] = useState(false);
  const [manualExternalCode, setManualExternalCode] = useState("");
  const [manualExternalTitle, setManualExternalTitle] = useState("");
  const [manualExternalUnitLoad, setManualExternalUnitLoad] = useState("");
  const [selectedExternalFileName, setSelectedExternalFileName] = useState("");

  const [students, setStudents] = useState([]);
  const [skipReport, setSkipReport] = useState(null);
  const [uploadSummary, setUploadSummary] = useState(null);
  const [error, setError] = useState(null);
  const [collapsed, setCollapsed] = useState(false);

  const abortControllerRef = useRef(null);
  const lastFetchRef = useRef(null);

  const { getCoursesForLevel, getUnitLoad, getExternalCourses } =
    useCatalogue();

  const current_semester = useMemo(
    () => getCoursesForLevel(level, semester),
    [level, semester, getCoursesForLevel],
  );
  const course_codes = useMemo(
    () => Object.keys(current_semester),
    [current_semester],
  );
  const course_titles = useMemo(
    () => Object.values(current_semester),
    [current_semester],
  );

  const external_courses_map = useMemo(
    () => getExternalCourses(),
    [getExternalCourses],
  );
  const external_codes = useMemo(
    () => Object.keys(external_courses_map),
    [external_courses_map],
  );
  const external_titles = useMemo(
    () => Object.values(external_courses_map),
    [external_courses_map],
  );

  const resolvedExternalCode = isManualExternal
    ? manualExternalCode
    : selectedExternalCourse;
  const resolvedExternalTitle = isManualExternal
    ? manualExternalTitle
    : external_courses_map[selectedExternalCourse];
  const resolvedExternalUnitLoad = isManualExternal
    ? Number(manualExternalUnitLoad)
    : Number(getUnitLoad(selectedExternalCourse));

  // ── fetch students ──────────────────────────────────────────────────────────
  const fetchStudents = useCallback(
    async (forceRefresh = false) => {
      if (!class_id || !semester || !level || !session) return;
      const cacheKey = `${class_id}-${semester}-${level}-${session}`;
      const now = Date.now();
      if (!forceRefresh && dataCache.has(cacheKey)) {
        const cached = dataCache.get(cacheKey);
        if (now - cached.timestamp < CACHE_DURATION) {
          setStudents(cached.data);
          return;
        }
      }
      if (abortControllerRef.current) abortControllerRef.current.abort();
      abortControllerRef.current = new AbortController();
      const requestTime = now;
      lastFetchRef.current = requestTime;
      setLoad(true);
      setError(null);
      try {
        let studentsData = [];
        try {
          const params = new URLSearchParams({
            class_id,
            semester,
            level,
            session,
          });
          const response = await authFetch(
            `${API_BASE_URL}/api/class/fetch?${params}`,
            { signal: abortControllerRef.current.signal },
          );
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          const text = await response.text();
          if (text.trim().startsWith("<")) throw new Error("HTML response");
          const json = JSON.parse(text);
          studentsData = json.students || [];
        } catch (apiErr) {
          if (apiErr.name === "AbortError") return;
          // Fall back to IndexedDB
          const { getClass } = await import("../../../utils/offlineQuery");
          const json = await getClass({ class_id, session, semester, level });
          studentsData = json.students || [];
        }
        if (lastFetchRef.current === requestTime) {
          setStudents(studentsData);
          dataCache.set(cacheKey, { data: studentsData, timestamp: now });
        }
      } catch (err) {
        if (err.name === "AbortError") return;
        if (lastFetchRef.current === requestTime) {
          setError(err.message);
          setStudents([]);
        }
      } finally {
        if (lastFetchRef.current === requestTime) setLoad(false);
      }
    },
    [class_id, semester, level, session],
  );

  useEffect(() => {
    fetchStudents();
    return () => abortControllerRef.current?.abort();
  }, [fetchStudents]);

  // ── file upload ─────────────────────────────────────────────────────────────
  const handleFileUpload = useCallback(
    (e, type = "main") => {
      const file = e.target.files[0];
      if (!file) return;
      if (type === "external") setSelectedExternalFileName(file.name);
      else setSelectedFileName(file.name);
      parseExcel(file);
    },
    [parseExcel],
  );

  // ── summary helper ──────────────────────────────────────────────────────────
  const applySummary = (json, label, rowCount) => {
    const skipped = Number(json?.summary?.skipped || 0);
    const skippedDetails = Array.isArray(json?.summary?.skipped_details)
      ? json.summary.skipped_details
      : [];
    const processed = Number(json?.summary?.processed || 0);
    const total = Number(json?.summary?.total_rows || rowCount || 0);
    const successMessage = `${label}: ${processed}/${total} registered successfully.`;
    const warningLines = [
      ...(skipped > 0
        ? [`${skipped} row(s) were skipped. See details below.`]
        : []),
      ...(Array.isArray(json?.summary?.warnings) ? json.summary.warnings : []),
    ];
    setUploadSummary({ success: successMessage, warnings: warningLines });
    if (skipped > 0)
      setSkipReport({ type: label, total, processed, skipped, skippedDetails });
    else setSkipReport(null);
    setAlert(true, successMessage, "success");
  };

  // ── register professional course ────────────────────────────────────────────
  const handle_course_reg = useCallback(async () => {
    if (!selectedCourse || !data?.length) {
      setAlert(
        true,
        "Please select a course and upload an Excel file.",
        "error",
      );
      return;
    }
    setSkipReport(null);
    setUploadSummary(null);
    setLoad(true);
    setError(null);
    try {
      const response = await authFetch(`${API_BASE_URL}/api/class/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          students: data,
          level,
          course_title: current_semester[selectedCourse],
          course_code: selectedCourse,
          unit_load: getUnitLoad(selectedCourse),
          semester,
          session,
          class_id,
          external: false,
        }),
      });
      const json = await response.json();
      if (!response.ok)
        throw new Error(
          json?.message || `HTTP error! status: ${response.status}`,
        );
      setStudents(json.students || []);
      dataCache.delete(`${class_id}-${semester}-${level}-${session}`);
      applySummary(json, "Course registration", data.length);
      setSelectedCourse("");
    } catch (err) {
      setError(err.message);
      setAlert(true, err.message || "Failed to register students.", "error");
    } finally {
      setLoad(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedCourse,
    data,
    level,
    semester,
    session,
    class_id,
    current_semester,
    getUnitLoad,
  ]);

  // ── register external course ────────────────────────────────────────────────
  const handle_external_course = useCallback(async () => {
    const courseCode = String(resolvedExternalCode || "").trim();
    const courseTitle = String(resolvedExternalTitle || "").trim();
    const unitLoad = resolvedExternalUnitLoad;
    if (!data?.length) {
      setAlert(true, "Please upload an Excel file.", "error");
      return;
    }
    if (
      !courseCode ||
      !courseTitle ||
      !Number.isFinite(unitLoad) ||
      unitLoad <= 0
    ) {
      setAlert(
        true,
        "Please provide a valid course code, title, and unit load.",
        "error",
      );
      return;
    }
    setSkipReport(null);
    setUploadSummary(null);
    setLoad(true);
    setError(null);
    try {
      const response = await authFetch(`${API_BASE_URL}/api/class/external`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          students: data,
          level,
          course_title: courseTitle,
          course_code: courseCode,
          unit_load: unitLoad,
          semester,
          session,
          class_id,
          external: true,
        }),
      });
      const json = await response.json();
      if (!response.ok)
        throw new Error(
          json?.message || `HTTP error! status: ${response.status}`,
        );
      setStudents(json.students || []);
      dataCache.delete(`${class_id}-${semester}-${level}-${session}`);
      applySummary(json, "External course registration", data.length);
      setSelectedExternalCourse("");
      setManualExternalCode("");
      setManualExternalTitle("");
      setManualExternalUnitLoad("");
      setShowExternal(false);
    } catch (err) {
      setError(err.message);
      setAlert(
        true,
        err.message || "Failed to register external course.",
        "error",
      );
    } finally {
      setLoad(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    resolvedExternalCode,
    resolvedExternalTitle,
    resolvedExternalUnitLoad,
    data,
    level,
    semester,
    session,
    class_id,
  ]);

  const semesterLabel = semester === "1" ? "First Semester" : "Second Semester";

  if (error) {
    return (
      <div className="level_page">
        <div className="level_state_card">
          <h3>Error loading data</h3>
          <p>{error}</p>
          <button
            className="level_btn level_btn_primary"
            onClick={() => fetchStudents(true)}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="level_page">
      {/* ── page header ── */}
      <section className="level_page_header">
        <div>
          <p className="level_kicker">Course Registration</p>
          <h2>
            {level} Level · {semesterLabel}
          </h2>
          <p className="level_session_label">{session}</p>
        </div>
        <div className="level_header_stats">
          <div className="level_stat">
            <span className="level_stat_value">{students.length}</span>
            <span className="level_stat_label">Students</span>
          </div>
          <div className="level_stat">
            <span className="level_stat_value">{data?.length || 0}</span>
            <span className="level_stat_label">Rows loaded</span>
          </div>
        </div>
      </section>

      {/* ── register professional course ── */}
      <section className="level_panel">
        <div className="level_panel_header">
          <div>
            <h3>Register Course</h3>
          </div>
          <button
            className="level_collapse_btn"
            onClick={() => setCollapsed((v) => !v)}
            aria-expanded={!collapsed}
          >
            {collapsed ? "Expand ↓" : "Collapse ↑"}
          </button>
        </div>

        {!collapsed && (
          <div className="level_panel_body">
            <div className="level_inline_row">
              <select
                value={selectedCourse}
                onChange={(e) => setSelectedCourse(e.target.value)}
                className="level_select level_select--grow"
                disabled={load}
              >
                <option value="">— select course —</option>
                {course_codes.map((code, i) => (
                  <option key={code} value={code}>
                    {code.toUpperCase()} · {course_titles[i]}
                  </option>
                ))}
              </select>

              <span className="level_unit_badge">
                {getUnitLoad(selectedCourse) ?? "—"} unit
                {getUnitLoad(selectedCourse) !== 1 ? "s" : ""}
              </span>

              <input
                id="main-file"
                type="file"
                accept=".xlsx,.xls"
                className="level_hidden_file_input"
                onChange={(e) => handleFileUpload(e, "main")}
                disabled={load}
              />
              <label htmlFor="main-file" className="level_file_btn">
                {selectedFileName ? (
                  <>
                    <span className="level_file_dot" /> {selectedFileName}{" "}
                    <span className="level_file_count">
                      ({data?.length || 0})
                    </span>
                  </>
                ) : (
                  "Choose file"
                )}
              </label>

              <button
                className="level_btn level_btn_primary"
                onClick={handle_course_reg}
                disabled={!selectedCourse || !data?.length || load}
              >
                {load ? "…" : "Register"}
              </button>

              <button
                className="level_btn level_btn_ghost"
                onClick={() => setShowExternal((v) => !v)}
                disabled={load}
              >
                {showExternal ? "Hide External" : "+ External Course"}
              </button>
            </div>
          </div>
        )}
      </section>

      {/* ── register external course ── */}
      {showExternal && (
        <section className="level_panel level_panel_ext">
          <div className="level_panel_header">
            <div>
              <h3>External Course</h3>
            </div>
            <div
              style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
            >
              <div className="level_toggle_row">
                <button
                  className={`level_toggle_btn ${!isManualExternal ? "active" : ""}`}
                  onClick={() => setIsManualExternal(false)}
                  disabled={load}
                >
                  Catalogue
                </button>
                <button
                  className={`level_toggle_btn ${isManualExternal ? "active" : ""}`}
                  onClick={() => setIsManualExternal(true)}
                  disabled={load}
                >
                  Manual
                </button>
              </div>
              <button
                className="level_close_btn"
                onClick={() => {
                  setShowExternal(false);
                  setSelectedExternalCourse("");
                  setIsManualExternal(false);
                  setManualExternalCode("");
                  setManualExternalTitle("");
                  setManualExternalUnitLoad("");
                }}
                aria-label="Close"
              >
                ✕
              </button>
            </div>
          </div>

          <div className="level_panel_body">
            <div className="level_inline_row">
              {!isManualExternal ? (
                <select
                  value={selectedExternalCourse}
                  onChange={(e) => setSelectedExternalCourse(e.target.value)}
                  className="level_select level_select--grow"
                  disabled={load}
                >
                  <option value="">— select course —</option>
                  {external_codes.map((code, i) => (
                    <option key={code} value={code}>
                      {code.toUpperCase()} · {external_titles[i]}
                    </option>
                  ))}
                </select>
              ) : (
                <>
                  <input
                    type="text"
                    className="level_input level_input--sm"
                    placeholder="Code e.g. CHM101"
                    value={manualExternalCode}
                    onChange={(e) =>
                      setManualExternalCode(e.target.value.toUpperCase())
                    }
                    disabled={load}
                  />
                  <input
                    type="text"
                    className="level_input level_select--grow"
                    placeholder="Course title"
                    value={manualExternalTitle}
                    onChange={(e) => setManualExternalTitle(e.target.value)}
                    disabled={load}
                  />
                </>
              )}

              {isManualExternal ? (
                <input
                  type="number"
                  className="level_input level_input--sm"
                  placeholder="Units"
                  min="1"
                  step="0.5"
                  value={manualExternalUnitLoad}
                  onChange={(e) => setManualExternalUnitLoad(e.target.value)}
                  disabled={load}
                />
              ) : (
                <span className="level_unit_badge">
                  {getUnitLoad(selectedExternalCourse) ?? "—"} units
                </span>
              )}

              <input
                id="ext-file"
                type="file"
                accept=".xlsx,.xls"
                className="level_hidden_file_input"
                onChange={(e) => handleFileUpload(e, "external")}
                disabled={load}
              />
              <label htmlFor="ext-file" className="level_file_btn">
                {selectedExternalFileName ? (
                  <>
                    <span className="level_file_dot" />{" "}
                    {selectedExternalFileName}{" "}
                    <span className="level_file_count">
                      ({data?.length || 0})
                    </span>
                  </>
                ) : (
                  "Choose file"
                )}
              </label>

              <button
                className="level_btn level_btn_primary"
                onClick={handle_external_course}
                disabled={
                  !data?.length ||
                  load ||
                  !String(resolvedExternalCode || "").trim() ||
                  !String(resolvedExternalTitle || "").trim() ||
                  !Number.isFinite(resolvedExternalUnitLoad) ||
                  resolvedExternalUnitLoad <= 0
                }
              >
                {load ? "…" : "Register"}
              </button>
            </div>
          </div>
        </section>
      )}

      {load && <Loader />}

      {/* ── upload summary ── */}
      {uploadSummary && (
        <section className="level_upload_summary_card">
          <p className="level_upload_summary_success">
            {uploadSummary.success}
          </p>
          {uploadSummary.warnings?.map((w, i) => (
            <p key={i} className="level_upload_summary_warning">
              {w}
            </p>
          ))}
        </section>
      )}

      {/* ── skip report ── */}
      {skipReport?.skipped > 0 && (
        <section className="level_skip_card">
          <div className="level_skip_head">
            <h3>Skipped Rows</h3>
            <p>
              {skipReport.type} · {skipReport.processed}/{skipReport.total}{" "}
              processed · {skipReport.skipped} skipped
            </p>
          </div>
          <div className="level_skip_grid">
            {skipReport.skippedDetails.map((item, i) => (
              <div
                key={`${item?.reg_no || "unknown"}-${i}`}
                className="level_skip_item"
              >
                <span className="level_skip_reg">{item?.reg_no || "N/A"}</span>
                <span className="level_skip_reason">
                  {item?.reason || "Skipped"}
                </span>
              </div>
            ))}
          </div>
          {skipReport.skipped > skipReport.skippedDetails.length && (
            <p className="level_skip_more">
              Showing {skipReport.skippedDetails.length} of {skipReport.skipped}{" "}
              skipped rows.
            </p>
          )}
        </section>
      )}

      {/* ── students table ── */}
      <section className="level_table_card">
        <div className="level_table_head">
          <h3>Registered Students</h3>
          <span>{students.length} students</span>
        </div>
        {students.length > 0 && <Table students={students} />}
        {!load && students.length === 0 && (
          <div className="level_empty_state">
            <p>No students registered for this class and semester yet.</p>
          </div>
        )}
      </section>
    </div>
  );
}

export default React.memo(Level);
